import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { useWebSocketContext } from '@/context/WebSocketProvider';
import {
  getGroupUnreadCounts,
  GroupUnreadCountsResponse,
  UnreadGroupInfo,
} from '@/services/api/chat/api';

export const useUnreadMessages = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { lastGroupChatMessage } = useWebSocketContext();
  const lastProcessedRef = useRef<string | null>(null);
  const [mentionFlash, setMentionFlash] = useState(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading } = useQuery<GroupUnreadCountsResponse>({
    queryKey: ['groupUnreadCounts'],
    queryFn: getGroupUnreadCounts,
    staleTime: 30_000, // 30 seconds
  });

  const triggerFlash = useCallback(() => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setMentionFlash(true);
    flashTimerRef.current = setTimeout(() => setMentionFlash(false), 3000);
  }, []);

  // Clean up timer on unmount
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );

  // Listen to WebSocket group messages and optimistically update unread counts
  useEffect(() => {
    if (!lastGroupChatMessage) return;

    const { data: message, groupId } = lastGroupChatMessage;

    // Deduplicate: skip if we already processed this message
    if (lastProcessedRef.current === message.messageId) return;
    lastProcessedRef.current = message.messageId;

    // Trigger mention flash for incoming messages that mention the current user
    const isOwnMessage = user && message.userId === user.id;
    if (
      !isOwnMessage &&
      message.mentions?.some((m) => m.userId === user?.id || m.type === 'group')
    ) {
      triggerFlash();
    }

    queryClient.setQueryData<GroupUnreadCountsResponse>(['groupUnreadCounts'], (oldData) => {
      if (!oldData) return oldData;

      const existingGroupIndex = oldData.groups.findIndex((g) => g.groupId === groupId);

      if (existingGroupIndex >= 0) {
        const updatedGroups = [...oldData.groups];
        const existingGroup = updatedGroups[existingGroupIndex];

        const newRecentMessage = {
          messageId: message.messageId,
          content: message.content,
          senderName: message.senderName,
          createdAt: message.createdAt,
        };

        const existing = existingGroup.recentMessages ?? [];
        // Prefer messageId equality; fall back to content+sender+timestamp for
        // entries that predate the messageId field.
        const isDuplicate = existing.some((m) =>
          m.messageId && newRecentMessage.messageId
            ? m.messageId === newRecentMessage.messageId
            : m.content === newRecentMessage.content &&
              m.senderName === newRecentMessage.senderName &&
              Math.abs(m.createdAt - newRecentMessage.createdAt) < 2000,
        );

        updatedGroups[existingGroupIndex] = {
          ...existingGroup,
          unreadCount: existingGroup.unreadCount + (isDuplicate || isOwnMessage ? 0 : 1),
          recentMessages: isDuplicate ? existing : [...existing.slice(-2), newRecentMessage],
        };

        // Sort by latest message timestamp (newest first)
        updatedGroups.sort((a, b) => {
          const aMessages = a.recentMessages ?? [];
          const bMessages = b.recentMessages ?? [];
          const aTime = aMessages[aMessages.length - 1]?.createdAt || 0;
          const bTime = bMessages[bMessages.length - 1]?.createdAt || 0;
          return bTime - aTime;
        });

        return {
          groups: updatedGroups,
          totalUnread: oldData.totalUnread + (isDuplicate || isOwnMessage ? 0 : 1),
        };
      } else {
        queryClient.invalidateQueries({ queryKey: ['groupUnreadCounts'] });
        return oldData;
      }
    });
  }, [lastGroupChatMessage, queryClient, user, triggerFlash]);

  const invalidateUnreads = () => {
    queryClient.invalidateQueries({ queryKey: ['groupUnreadCounts'] });
  };

  return {
    totalUnread: data?.totalUnread ?? 0,
    groupUnreads: data?.groups ?? ([] as UnreadGroupInfo[]),
    isLoading,
    invalidateUnreads,
    mentionFlash,
  };
};
